/**
 * Single source of truth for turning an error thrown by a React Query
 * `queryFn`/`mutationFn` into a small, well-known classification.
 *
 * This replaces two previously-duplicated implementations of the same idea:
 * `ApiClientWrapper`'s status-code switch (libs/api) and
 * `useQueryWithParsedError`'s `parseError` (this lib) — both are gone now
 * that this is the one place status codes get interpreted.
 */

export type ApiErrorKind =
  | 'bad-request'
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'validation'
  | 'rate-limited'
  | 'server'
  | 'network'
  | 'service-unavailable'
  | 'session'
  | 'unknown';

export interface ClassifiedApiError {
  kind: ApiErrorKind;
  /** HTTP status code, when the error carries one (absent for network errors). */
  status?: number;
  /** Whether retrying the same request again is expected to eventually succeed. */
  retriable: boolean;
}

/**
 * Duck-types the shape the generated `api-spec` clients' `ResponseError`
 * classes (and plain `{ response: Response }` errors) share, without
 * depending on any specific generated package — this lib is used by apps
 * (`maritime-control-center`, `data-forge`) that don't use `api-spec` at all.
 */
function isResponseError(
  error: unknown,
): error is { response: { status: number } } {
  return (
    error !== null &&
    typeof error === 'object' &&
    'response' in error &&
    error.response !== null &&
    typeof error.response === 'object' &&
    'status' in error.response &&
    typeof (error.response as Record<string, unknown>).status === 'number'
  );
}

const NETWORK_ERROR_MESSAGE_PATTERN =
  /failed to fetch|networkerror|network error|load failed|the request failed/i;

/** True when the request never received an HTTP response at all. */
function isNetworkError(error: unknown): boolean {
  if (isResponseError(error)) return false;

  const name = (error as { name?: unknown } | null)?.name;
  if (name === 'FetchError') return true;

  // A bare `instanceof TypeError` check is not enough: any programming bug in
  // a queryFn (e.g. reading a property of `undefined`) throws a TypeError, and
  // that must surface as `unknown` — not as a retriable "connection problem".
  // Browser fetch failures are TypeErrors with a known message, so match on
  // the message instead of the type.
  const message = error instanceof Error ? error.message : '';
  return NETWORK_ERROR_MESSAGE_PATTERN.test(message);
}

/**
 * Duck-types `libs/api`'s `ServiceUnavailableError` by name (this lib must
 * not depend on libs/api). The API client wrapper's circuit breaker converts
 * 401/404 responses on mapped services into this error; the original status
 * is preserved on the error for classification/telemetry.
 */
function isServiceUnavailableError(
  error: unknown,
): error is { name: string; status?: number } {
  return error instanceof Error && error.name === 'ServiceUnavailableError';
}

/**
 * Duck-types `@ais-ui/auth`'s `SessionUnavailableError` by name. It is thrown
 * by the websocket session's `request()` when a JSON-RPC call fails because
 * the session was rejected (invalid/expired/superseded) or the transport was
 * unavailable — not because of the request itself. These are owned by the
 * reconnect / grace-period flow, so they must not be toasted per failing
 * query; hence the `session` kind (see `createAppQueryClient`).
 */
function isSessionUnavailableError(error: unknown): boolean {
  return error instanceof Error && error.name === 'SessionUnavailableError';
}

export function classifyApiError(error: unknown): ClassifiedApiError {
  // A session/transport failure behind a websocket RPC — the reconnect flow
  // owns recovery and any user-facing feedback. Deterministic until the
  // transport is reconnected, so never retriable and never toasted.
  if (isSessionUnavailableError(error)) {
    return { kind: 'session', retriable: false };
  }

  // The circuit breaker's ServiceUnavailableError wraps 401/404s on mapped
  // services. Check it before the network/unknown fallbacks — it is
  // deterministic (never retriable) and owns its own UX.
  if (isServiceUnavailableError(error)) {
    return {
      kind: 'service-unavailable',
      status: error.status,
      retriable: false,
    };
  }

  if (isResponseError(error)) {
    const status = error.response.status;

    switch (status) {
      case 400:
        return { kind: 'bad-request', status, retriable: false };
      case 401:
        return { kind: 'unauthorized', status, retriable: false };
      case 403:
        return { kind: 'forbidden', status, retriable: false };
      case 404:
        return { kind: 'not-found', status, retriable: false };
      case 409:
        return { kind: 'conflict', status, retriable: false };
      case 422:
        return { kind: 'validation', status, retriable: false };
      case 429:
        return { kind: 'rate-limited', status, retriable: false };
      default:
        if (status >= 500) {
          return { kind: 'server', status, retriable: true };
        }
        return { kind: 'unknown', status, retriable: false };
    }
  }

  if (isNetworkError(error)) {
    return { kind: 'network', retriable: true };
  }

  return { kind: 'unknown', retriable: false };
}
